import React from 'react'
import { Button, Card, CardContent, Modal } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreHorizontal } from 'lucide-react'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import { useToast } from '@/shared/ui/Toast'
import type { ChatItem } from '../services/chatService'
import { deleteChat, updateChat } from '../services/chatService'
import type { ChatCommunitySummary } from '../services/chatCommunityService'
import { ChatCommunityEntry } from './ChatCommunityEntry'
import { NewChatModal, type NewChatModalSubmitParams } from './NewChatModal'
import { deleteChatPostAttachmentsByIds } from '../services/chatPostAttachmentsService'
import { uploadAndAddChatPostAttachments } from '../services/chatPostAttachmentsUploadService'
import { PostContent, PostAttachments, PostCardAuthorMeta } from '@/modules/post-shared'
import { buildLinkPreview, extractFirstUrlFromHtml } from '@/modules/comunidade/services/linkPreviewService'
import { CommunityLinkPreviewCard } from '@/modules/comunidade/components/CommunityLinkPreviewCard'

type ChatCardProps = {
  chat: ChatItem
  communityEnabled: boolean
  currentUserId: string | null
  canDeleteOthers: boolean
  chatCommunitySummary?: ChatCommunitySummary | null
  focusMessageId?: number | null
  onChanged?: () => void
}

export function ChatCard({
  chat,
  communityEnabled,
  currentUserId,
  canDeleteOthers,
  chatCommunitySummary,
  focusMessageId,
  onChanged,
}: ChatCardProps) {
  const { formatUserDateTime } = useUserDateFormatter()
  const { add: toast } = useToast()
  const displayAuthorName = normalizeAuthorName(chat.authorName)

  const [editOpen, setEditOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const linkPreview = React.useMemo(() => {
    const url = extractFirstUrlFromHtml(String(chat.contentHtml || ''))
    if (!url) return null
    return buildLinkPreview(url)
  }, [chat.contentHtml])

  const isAuthor = currentUserId && chat.authorId && String(chat.authorId) === String(currentUserId)
  const canEdit = !!isAuthor
  const canDelete = !!isAuthor || canDeleteOthers
  const canManage = canEdit || canDelete

  async function handleEdit(params: NewChatModalSubmitParams) {
    await updateChat(chat.id, {
      title: params.title,
      html: params.html,
    })

    if (params.removeAttachmentIds.length > 0) {
      await deleteChatPostAttachmentsByIds(params.removeAttachmentIds)
    }

    if (params.newAttachments.length > 0) {
      const res = await uploadAndAddChatPostAttachments(chat.id, params.newAttachments)
      if (res.failedCount > 0) {
        toast({
          title: 'Alguns anexos falharam',
          description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
          variant: 'warning',
          soft: true,
        })
      }
    }

    if (onChanged) onChanged()
  }

  async function handleConfirmDelete() {
    if (deleting) return
    setDeleting(true)
    try {
      await deleteChat(chat.id)
      if (onChanged) onChanged()
      setDeleteOpen(false)
    } catch (e: any) {
      toast({
        title: 'Falha ao excluir',
        description: e?.message || 'Tente novamente em instantes.',
        variant: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  const menuItems = [
    canEdit
      ? {
          label: 'Editar',
          onSelect: () => setEditOpen(true),
        }
      : null,
    canDelete
      ? {
          label: 'Excluir',
          onSelect: () => setDeleteOpen(true),
        }
      : null,
  ].filter(Boolean) as { label: string; onSelect: () => void }[]

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold leading-tight text-foreground">
              {chat.title}
            </div>
          </div>
          {canManage && menuItems.length > 0 && (
            <div className="ml-2 flex items-start">
              <DropdownMenu
                align="end"
                trigger={(
                  <button
                    type="button"
                    className="inline-flex items-center rounded p-1 hover:bg-muted/30"
                  >
                    <MoreHorizontal className="h-5 w-5 text-[#434343]" />
                  </button>
                )}
                items={menuItems}
              />
            </div>
          )}
        </div>

        <div className="mt-3 min-w-0 space-y-3">
          <PostContent html={chat.contentHtml} />

          <PostAttachments attachments={chat.attachments} />

          {linkPreview && (
            <div>
              <CommunityLinkPreviewCard preview={linkPreview} />
            </div>
          )}
        </div>

        {(() => {
          const authorMeta = (
            <PostCardAuthorMeta
              name={displayAuthorName}
              avatarUrl={chat.authorAvatarUrl}
              dateLabel={chat.createdAt ? formatUserDateTime(chat.createdAt) : null}
            />
          )

          if (!communityEnabled) {
            return <div className="mt-3 flex justify-end">{authorMeta}</div>
          }

          return (
            <ChatCommunityEntry
              chatId={chat.id}
              title={chat.title}
              contentHtml={chat.contentHtml}
              attachments={chat.attachments}
              enabled={communityEnabled}
              summary={chatCommunitySummary}
              focusMessageId={focusMessageId}
              footerRight={authorMeta}
            />
          )
        })()}

        {canEdit && (
          <NewChatModal
            open={editOpen}
            onOpenChange={setEditOpen}
            mode="edit"
            chatId={chat.id}
            initialTitle={chat.title}
            initialHtml={chat.contentHtml}
            initialAttachments={chat.attachments}
            onSubmit={handleEdit}
          />
        )}
        <Modal
          open={deleteOpen}
          onOpenChange={(v) => {
            if (!v && !deleting) setDeleteOpen(false)
          }}
          dismissible={!deleting}
          className="max-w-md"
        >
          <div className="space-y-4">
            <div className="text-lg font-semibold">Excluir post</div>
            <p className="text-sm text-foreground/80">
              Tem certeza que deseja excluir este post?
            </p>
            <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-foreground/80">
              Essa exclusão não poderá ser desfeita.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </Modal>
      </CardContent>
    </Card>
  )
}
